import { Trans, useLingui } from '@lingui/react/macro'
import { maintenanceLogHooks, maintenanceTaskHooks } from '$src/lib/trpcHooks'
import {
  getCurrentSeason,
  getSeasonName,
  getTaskSeason,
} from '$src/modules/maintenance/YearCalendarCarousel/utils'
import { Button, Card, Group, Progress, Stack, Text } from '@mantine/core'
import type {
  MaintenanceLog,
  MaintenanceTask,
  PropertyWithRelations,
} from '@markku/types'
import { groupBy } from '@markku/utils'
import { useNavigate } from '@tanstack/react-router'

interface MaintenanceCardProps {
  isPremium: boolean
  property?: PropertyWithRelations
}

const getCompletedTasksCount = (
  tasks: MaintenanceTask[],
  logs: MaintenanceLog[],
  currentSeasonKey: string,
) => {
  const tasksBySeason = groupBy(tasks, getTaskSeason)

  const currentYear = new Date().getFullYear()
  const completedTaskIds = new Set(
    logs
      .filter((log) => {
        if (!log.completedDate) return false
        const completedYear = new Date(log.completedDate).getFullYear()
        return completedYear === currentYear
      })
      .map((log) => log.maintenanceTaskId),
  )

  const seasonTasks = tasksBySeason.get(currentSeasonKey) || []
  const completed = seasonTasks.filter((task) =>
    completedTaskIds.has(task.id),
  ).length

  return {
    completedCount: completed,
    totalCount: seasonTasks.length,
  }
}

export function MaintenanceCard({ property }: MaintenanceCardProps) {
  const { t } = useLingui()
  const navigate = useNavigate()
  const propertyId = property?.id

  const { data: tasksData } = maintenanceTaskHooks.useList({ pageSize: 100 })
  const { data: logsData } = maintenanceLogHooks.useList(
    {
      pageSize: 100,
      filters: { propertyId: { eq: propertyId! } },
    },
    { enabled: !!propertyId },
  )

  const currentSeasonKey = getCurrentSeason()

  const tasks = tasksData?.data || []
  const logs = logsData?.data || []

  const { completedCount, totalCount } = getCompletedTasksCount(
    tasks,
    logs,
    currentSeasonKey,
  )

  const progressPercent =
    totalCount > 0 ? (completedCount / totalCount) * 100 : 0

  return (
    <Card radius="lg" withBorder shadow="xs" p="md">
      <Stack gap="md">
        <Stack gap="xs">
          <Text size="md" fw={600}>
            {t(getSeasonName(currentSeasonKey))}
          </Text>
          <Group gap="xs">
            <Progress
              value={progressPercent}
              color="brandOrange"
              size="md"
              style={{ flex: 1 }}
              radius="xl"
            />
            <Text size="sm" c="dimmed">
              <Trans>
                {completedCount}/{totalCount} done
              </Trans>
            </Text>
          </Group>
        </Stack>

        <Group grow gap="xs">
          <Button
            size="sm"
            radius="md"
            color="brandOrange"
            variant="filled"
            onClick={() => navigate({ to: '/maintenance' })}
          >
            <Trans>Open maintenance book</Trans>
          </Button>
        </Group>
      </Stack>
    </Card>
  )
}
